import { useState } from 'react';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Filters,
  InlineStack,
  Modal,
  ResourceItem,
  ResourceList,
  Spinner,
  Text,
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { TemplatesEmptyState } from '../shared/EmptyStates';
import type { NotificationTemplate } from '@/queries/notifications';

export interface TemplatesTabProps {
  templates: NotificationTemplate[];
  isLoading: boolean;
  onLoad: (t: NotificationTemplate) => void;
  onDelete: (id: number) => void;
  deletePending: boolean;
  onGotoCompose: () => void;
}

export function TemplatesTab({
  templates,
  isLoading,
  onLoad,
  onDelete,
  deletePending,
  onGotoCompose,
}: TemplatesTabProps) {
  const [query, setQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<NotificationTemplate | null>(null);

  if (isLoading) {
    return (
      <Card>
        <InlineStack align="center"><Spinner accessibilityLabel="loading" /></InlineStack>
      </Card>
    );
  }

  if (!templates.length) {
    return (
      <Card>
        <TemplatesEmptyState onGotoCompose={onGotoCompose} />
      </Card>
    );
  }

  const filtered = query
    ? templates.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()))
    : templates;

  return (
    <>
      <Card padding="0">
        <ResourceList
          resourceName={{ singular: 'template', plural: 'templates' }}
          items={filtered}
          filterControl={
            <Filters
              queryValue={query}
              queryPlaceholder="ابحث في الـ templates…"
              onQueryChange={setQuery}
              onQueryClear={() => setQuery('')}
              filters={[]}
              onClearAll={() => setQuery('')}
            />
          }
          renderItem={(t) => (
            <TemplateItem
              template={t}
              onLoad={() => onLoad(t)}
              onDelete={() => setConfirmDelete(t)}
              deletePending={deletePending}
            />
          )}
        />
      </Card>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="تأكيد الحذف"
        primaryAction={{
          content: 'حذف نهائيًا',
          destructive: true,
          loading: deletePending,
          onAction: () => {
            if (confirmDelete) {
              onDelete(confirmDelete.id);
              setConfirmDelete(null);
            }
          },
        }}
        secondaryActions={[{ content: 'إلغاء', onAction: () => setConfirmDelete(null) }]}
      >
        <Modal.Section>
          <Text as="p">
            هتـ delete template بعنوان: <strong>{confirmDelete?.name}</strong>
          </Text>
        </Modal.Section>
      </Modal>
    </>
  );
}

interface TemplateItemProps {
  template: NotificationTemplate;
  onLoad: () => void;
  onDelete: () => void;
  deletePending: boolean;
}

function TemplateItem({ template: t, onLoad, onDelete, deletePending }: TemplateItemProps) {
  return (
    <ResourceItem id={String(t.id)} onClick={onLoad} accessibilityLabel={t.name}>
      <InlineStack align="space-between" blockAlign="center" wrap={false}>
        <Box minWidth="0" width="100%">
          <BlockStack gap="050">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h3" variant="headingSm">{t.name}</Text>
              <Badge tone="info">{`استُخدم ${t.use_count}× `}</Badge>
              {t.target_type ? <Badge>{t.target_type}</Badge> : null}
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued" truncate>
              <strong>{t.title}</strong> — {t.body.length > 60 ? t.body.slice(0, 60) + '…' : t.body}
            </Text>
          </BlockStack>
        </Box>
        <InlineStack gap="100">
          <Button onClick={onLoad}>تحميل في Compose</Button>
          <Button
            icon={DeleteIcon}
            variant="tertiary"
            tone="critical"
            onClick={onDelete}
            loading={deletePending}
          >
            حذف
          </Button>
        </InlineStack>
      </InlineStack>
    </ResourceItem>
  );
}
